import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Shield, Sword, Heart, Brain, Eye, Zap,
  Star, Lock, ChevronRight, Sparkles, Crown, Target,
} from 'lucide-react';

// ─── RPG Character Sheet for Servers ────────────────────────────────

interface Skill {
  name: string;
  level: number;
  maxLevel: number;
  xpCurrent: number;
  xpRequired: number;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  unlocked: boolean;
}

interface Ability {
  name: string;
  description: string;
  unlockLevel: number;
  unlocked: boolean;
}

const SKILLS: Skill[] = [
  { name: 'Charisma', level: 7, maxLevel: 10, xpCurrent: 340, xpRequired: 500, description: 'Your ability to connect with guests and make them feel welcome', icon: Heart, unlocked: true },
  { name: 'Hustle', level: 6, maxLevel: 10, xpCurrent: 280, xpRequired: 400, description: 'Speed and efficiency during rush hours', icon: Zap, unlocked: true },
  { name: 'Menu Mastery', level: 8, maxLevel: 10, xpCurrent: 420, xpRequired: 500, description: 'Deep knowledge of every item, pairing, and special', icon: Brain, unlocked: true },
  { name: 'The Close', level: 5, maxLevel: 10, xpCurrent: 180, xpRequired: 350, description: 'Converting suggestions into actual orders', icon: Target, unlocked: true },
  { name: 'Read the Room', level: 4, maxLevel: 10, xpCurrent: 120, xpRequired: 300, description: 'Knowing when to push and when to back off', icon: Eye, unlocked: true },
  { name: 'Resilience', level: 6, maxLevel: 10, xpCurrent: 250, xpRequired: 400, description: 'Bouncing back from tough tables and slow shifts', icon: Shield, unlocked: true },
];

const ABILITIES: Ability[] = [
  { name: 'Warm Welcome', description: 'Your greetings are naturally engaging', unlockLevel: 3, unlocked: true },
  { name: 'Silver Tongue', description: '+10% upsell success on premium items', unlockLevel: 5, unlocked: true },
  { name: 'Drink Whisperer', description: 'Drink promotion success rate doubled', unlockLevel: 7, unlocked: true },
  { name: 'Streak Shield', description: 'One miss doesn\'t break your streak', unlockLevel: 9, unlocked: false },
  { name: 'Table Turner', description: 'Bonus points during rush hour', unlockLevel: 12, unlocked: false },
  { name: 'Legendary Closer', description: 'Triple points on group upsells (4+ guests)', unlockLevel: 15, unlocked: false },
  { name: 'Mentor\'s Aura', description: 'Nearby servers get +5% when you\'re on shift', unlockLevel: 20, unlocked: false },
  { name: 'Diamond Hands', description: 'Points never decay, even on off weeks', unlockLevel: 25, unlocked: false },
];

interface Equipment {
  slot: string;
  name: string;
  bonus: string;
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
}

const LOADOUT: Equipment[] = [
  { slot: 'Opening Line', name: '"Have you been here before?"', bonus: '+12% engagement rate', rarity: 'rare' },
  { slot: 'Closer', name: '"You won\'t regret the Wagyu"', bonus: '+18% conversion on premium', rarity: 'epic' },
  { slot: 'Recovery', name: '"Totally get it — the regular menu is great too"', bonus: '+8% save rate on declines', rarity: 'rare' },
  { slot: 'Drink Pitch', name: '"Our Mango Lassi is a hidden gem"', bonus: '+22% drink promo success', rarity: 'legendary' },
];

const rarityColors = {
  common: { text: 'text-gray-500', bg: 'bg-gray-100', border: 'border-gray-300' },
  rare: { text: 'text-blue-500', bg: 'bg-blue-50', border: 'border-blue-300' },
  epic: { text: 'text-purple-500', bg: 'bg-purple-50', border: 'border-purple-300' },
  legendary: { text: 'text-amber-500', bg: 'bg-amber-50', border: 'border-amber-300' },
};

const CharacterSheet: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'skills' | 'abilities' | 'loadout'>('skills');

  const totalLevel = SKILLS.reduce((s, sk) => s + sk.level, 0);
  const className = totalLevel >= 40 ? 'Grand Master' : totalLevel >= 30 ? 'Expert' : totalLevel >= 20 ? 'Journeyman' : 'Apprentice';

  return (
    <div className="min-h-screen bg-gray-950">
      {/* Header — parchment style on dark bg */}
      <div className="bg-gradient-to-b from-gray-900 to-gray-950 border-b border-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <button onClick={() => navigate('/restaurant/mockups')} className="flex items-center gap-1.5 text-gray-500 hover:text-gray-300 text-sm mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Hub
          </button>

          <div className="flex items-center gap-6">
            {/* Character portrait */}
            <div className="relative">
              <div className="w-24 h-24 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-3xl font-black text-white border-2 border-indigo-400 shadow-lg shadow-indigo-500/20">
                YO
              </div>
              <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-indigo-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap border border-indigo-400">
                LVL 14
              </div>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-1">
                <h1 className="text-xl font-bold text-white">You</h1>
                <span className="text-xs font-medium text-indigo-400 bg-indigo-400/10 px-2 py-0.5 rounded-full">
                  {className}
                </span>
              </div>
              <p className="text-sm text-gray-400 mb-2">Server · Gen Korean BBQ, Fremont</p>
              <div className="flex items-center gap-4 text-xs text-gray-500">
                <span>Combined Skill: <span className="text-white font-bold">{totalLevel}</span></span>
                <span>Abilities: <span className="text-white font-bold">{ABILITIES.filter(a => a.unlocked).length}/{ABILITIES.length}</span></span>
                <span>Loadout: <span className="text-white font-bold">{LOADOUT.length} equipped</span></span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Tab bar */}
        <div className="flex items-center gap-1 bg-gray-900 rounded-lg p-1 border border-gray-800 w-fit">
          {[
            { key: 'skills' as const, label: 'Skill Tree', icon: Sword },
            { key: 'abilities' as const, label: 'Abilities', icon: Sparkles },
            { key: 'loadout' as const, label: 'Loadout', icon: Shield },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-md transition-colors ${
                activeTab === tab.key ? 'bg-gray-800 text-white' : 'text-gray-500 hover:text-gray-300'
              }`}
            >
              <tab.icon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Skills tab */}
        {activeTab === 'skills' && (
          <div className="space-y-3">
            {SKILLS.map((skill) => {
              const pct = (skill.xpCurrent / skill.xpRequired) * 100;
              return (
                <div key={skill.name} className="bg-gray-900 rounded-xl border border-gray-800 p-5">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center flex-shrink-0">
                      <skill.icon className="w-6 h-6 text-indigo-400" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <h3 className="text-sm font-semibold text-white">{skill.name}</h3>
                          <div className="flex items-center gap-0.5">
                            {Array.from({ length: skill.maxLevel }).map((_, i) => (
                              <div
                                key={i}
                                className={`w-1.5 h-4 rounded-sm ${
                                  i < skill.level ? 'bg-indigo-400' : 'bg-gray-800'
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                        <span className="text-sm font-bold text-indigo-400">Lvl {skill.level}</span>
                      </div>
                      <p className="text-xs text-gray-500 mb-2">{skill.description}</p>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-gray-800 rounded-full overflow-hidden">
                          <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-[10px] text-gray-600">{skill.xpCurrent}/{skill.xpRequired} XP</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Stat hexagon summary */}
            <div className="bg-gray-900 rounded-xl border border-gray-800 p-6 text-center">
              <h3 className="text-sm font-semibold text-gray-400 mb-4 uppercase tracking-wider">Stat Distribution</h3>
              <div className="relative w-48 h-48 mx-auto">
                <svg viewBox="0 0 200 200" className="w-full h-full">
                  {/* Background hexagon */}
                  <polygon
                    points="100,10 180,55 180,145 100,190 20,145 20,55"
                    fill="none"
                    stroke="rgb(31,41,55)"
                    strokeWidth="1"
                  />
                  <polygon
                    points="100,46 152,73 152,127 100,154 48,127 48,73"
                    fill="none"
                    stroke="rgb(31,41,55)"
                    strokeWidth="1"
                  />
                  {/* Filled area */}
                  {(() => {
                    const levels = SKILLS.map(s => s.level / s.maxLevel);
                    const cx = 100, cy = 100, r = 80;
                    const points = levels.map((l, i) => {
                      const angle = (Math.PI * 2 * i) / levels.length - Math.PI / 2;
                      return `${cx + r * l * Math.cos(angle)},${cy + r * l * Math.sin(angle)}`;
                    }).join(' ');
                    return (
                      <polygon points={points} fill="rgba(99,102,241,0.2)" stroke="rgb(99,102,241)" strokeWidth="2" />
                    );
                  })()}
                  {/* Labels */}
                  {SKILLS.map((skill, i) => {
                    const angle = (Math.PI * 2 * i) / SKILLS.length - Math.PI / 2;
                    const lx = 100 + 95 * Math.cos(angle);
                    const ly = 100 + 95 * Math.sin(angle);
                    return (
                      <text key={skill.name} x={lx} y={ly} textAnchor="middle" dominantBaseline="middle" className="fill-gray-500 text-[9px]">
                        {skill.name}
                      </text>
                    );
                  })}
                </svg>
              </div>
            </div>
          </div>
        )}

        {/* Abilities tab */}
        {activeTab === 'abilities' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {ABILITIES.map((ability, i) => (
              <div
                key={i}
                className={`rounded-xl border p-4 transition-all ${
                  ability.unlocked
                    ? 'bg-gray-900 border-indigo-500/30 hover:border-indigo-400/50'
                    : 'bg-gray-950 border-gray-800 opacity-50'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  {ability.unlocked ? (
                    <Sparkles className="w-4 h-4 text-indigo-400" />
                  ) : (
                    <Lock className="w-4 h-4 text-gray-600" />
                  )}
                  <h3 className={`text-sm font-semibold ${ability.unlocked ? 'text-white' : 'text-gray-600'}`}>
                    {ability.name}
                  </h3>
                </div>
                <p className={`text-xs mb-2 ${ability.unlocked ? 'text-gray-400' : 'text-gray-700'}`}>
                  {ability.description}
                </p>
                <span className={`text-[10px] font-medium ${ability.unlocked ? 'text-indigo-400' : 'text-gray-700'}`}>
                  {ability.unlocked ? 'Active' : `Unlocks at Level ${ability.unlockLevel}`}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Loadout tab */}
        {activeTab === 'loadout' && (
          <div className="space-y-3">
            <p className="text-xs text-gray-500 mb-2">
              Your equipped phrases and techniques. These are your go-to moves — the ones with the best stats from your actual performance.
            </p>
            {LOADOUT.map((item, i) => {
              const rc = rarityColors[item.rarity];
              return (
                <div key={i} className={`bg-gray-900 rounded-xl border ${rc.border} p-5`}>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 w-20">{item.slot}</span>
                      <span className={`text-[10px] font-bold uppercase ${rc.text}`}>{item.rarity}</span>
                    </div>
                  </div>
                  <h3 className="text-sm font-semibold text-white mb-1 italic">{item.name}</h3>
                  <p className={`text-xs ${rc.text} font-medium`}>{item.bonus}</p>
                </div>
              );
            })}
            <div className="bg-gray-900/50 rounded-xl border border-dashed border-gray-800 p-5 text-center">
              <p className="text-xs text-gray-600">More loadout slots unlock as you level up</p>
              <p className="text-[10px] text-gray-700 mt-1">Level 16: "Recovery Follow-up" · Level 20: "Group Opener"</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CharacterSheet;
